<template>
  <div class="form-page-wrapper">
    <!-- Hero Banner -->
    <div class="form-hero">
      <v-container>
        <v-layout wrap justify-center>
          <v-flex xs12 text-center py-4>
            <span class="oswaldbold cwhite hero-title">JOB APPLICATION</span>
            <div class="hero-underline mx-auto mt-1"></div>
            <p class="popsemibold cwhite hero-sub mt-2 mb-0">
              Join our mission to protect India's wildlife and wild spaces
            </p>
          </v-flex>
        </v-layout>
      </v-container>
    </div>

    <!-- Form Body -->
    <div class="form-body">
      <v-container fluid class="px-4 px-md-12 py-8">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-layout wrap>

            <!-- ─── COL 1: Personal & Contact ─────────────────────────── -->
            <v-flex xs12 md4 class="form-col pr-md-3 mb-5">
              <div class="section-card">
                <div class="section-head">
                  <v-icon left size="18" color="#ffffff">mdi-account-circle</v-icon>
                  <span class="popsemibold">PERSONAL &amp; CONTACT DETAILS</span>
                </div>
                <div class="section-body">

                  <v-layout wrap>
                    <v-flex xs6 class="pr-2 pb-2">
                      <div class="flabel popsemibold">First name *</div>
                      <v-text-field v-model="formData.firstName" :rules="reqRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                    <v-flex xs6 class="pl-2 pb-2">
                      <div class="flabel popsemibold">Last name *</div>
                      <v-text-field v-model="formData.lastName" :rules="reqRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap>
                    <v-flex xs7 class="pr-2 pb-2">
                      <div class="flabel popsemibold">Date of Birth *</div>
                      <v-menu
                        v-model="dobMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="formData.dob"
                            placeholder="Select Date"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :rules="reqRule"
                            append-icon="mdi-calendar"
                            dense outlined hide-details="auto" color="#005f32" class="wti-input calendar-input"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="formData.dob"
                          @input="dobMenu = false; calculateAge()"
                          color="#005f32"
                          width="230"
                          class="square-calendar"
                          no-title
                          :max="new Date().toISOString().substr(0, 10)"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>
                    <v-flex xs5 class="pl-2 pb-2 d-flex flex-column justify-center">
                      <div class="flabel popsemibold">Age</div>
                      <div class="age-chip popregular">{{ formData.age ? formData.age + ' yrs' : '—' }}</div>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap>
                    <v-flex xs12 class="pb-3">
                      <div class="flabel popsemibold">Email address *</div>
                      <v-text-field type="email" v-model="formData.email" :rules="emailRules" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap>
                    <v-flex xs4 class="pr-1 pb-3">
                      <div class="flabel popsemibold">Code *</div>
                      <v-select :items="countryCodes" item-text="label" item-value="code" v-model="formData.countryCode" :rules="reqRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-select>
                    </v-flex>
                    <v-flex xs8 class="pl-1 pb-3">
                      <div class="flabel popsemibold">Contact number *</div>
                      <v-text-field v-model="formData.contactNumber" :rules="phoneRules" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                  </v-layout>

                  <div class="field-group-label popsemibold mb-2">Location Details</div>
                  <v-layout wrap>
                    <v-flex xs6 class="pr-2 pb-1">
                      <div class="flabel popregular">Current — State</div>
                      <v-select :items="states" v-model="formData.currentLocationState" dense outlined hide-details color="#005f32" class="wti-input"></v-select>
                    </v-flex>
                    <v-flex xs6 class="pl-2 pb-1">
                      <div class="flabel popregular">Current — City</div>
                      <v-text-field v-model="formData.currentLocationCity" dense outlined hide-details color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                    <v-flex xs6 class="pr-2 pb-2 pt-2">
                      <div class="flabel popregular">Permanent — State</div>
                      <v-select :items="states" v-model="formData.permanentLocationState" dense outlined hide-details color="#005f32" class="wti-input"></v-select>
                    </v-flex>
                    <v-flex xs6 class="pl-2 pb-2 pt-2">
                      <div class="flabel popregular">Permanent — City</div>
                      <v-text-field v-model="formData.permanentLocationCity" dense outlined hide-details color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap>
                    <v-flex xs12 class="pb-3">
                      <div class="flabel popsemibold">Willing to relocate? *</div>
                      <v-radio-group v-model="formData.willingToRelocate" row class="mt-1" hide-details :rules="[v => v !== null || 'Required']">
                        <v-radio label="Yes" :value="true" color="#005f32"></v-radio>
                        <v-radio label="No" :value="false" color="#005f32"></v-radio>
                      </v-radio-group>
                    </v-flex>
                    <v-flex xs12 class="pb-3" v-if="formData.willingToRelocate !== false">
                      <div class="flabel popsemibold mb-1">Preferred Locations (Upto 3)</div>
                      <div v-for="(loc, index) in formData.preferredLocations" :key="index" class="mb-2 pa-2" style="background: #f8fbf9; border: 1px solid #e0ece6; border-radius: 6px;">
                        <v-layout wrap align-center>
                          <v-flex xs5 class="pr-1">
                            <v-select :items="states" v-model="loc.state" placeholder="State" dense outlined hide-details color="#005f32" class="wti-input"></v-select>
                          </v-flex>
                          <v-flex xs5 class="px-1">
                            <v-text-field v-model="loc.city" placeholder="City" dense outlined hide-details color="#005f32" class="wti-input"></v-text-field>
                          </v-flex>
                          <v-flex xs2 text-right>
                            <v-icon v-if="formData.preferredLocations.length > 1" @click="removeLocation(index)" color="#c0392b" style="cursor: pointer" size="20">mdi-delete-outline</v-icon>
                          </v-flex>
                        </v-layout>
                      </div>
                      <v-btn v-if="formData.preferredLocations.length < 3" text small color="#005f32" @click="addLocation" class="px-0 mt-1 popsemibold" style="text-transform: none; letter-spacing: 0;">
                        <v-icon left small>mdi-plus</v-icon> Add another location
                      </v-btn>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap>
                    <v-flex xs12 class="pb-3">
                      <div class="flabel popsemibold">How did you hear about this vacancy? *</div>
                      <v-select :items="hearSources" v-model="formData.howDidYouHear" :rules="reqRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-select>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap v-if="formData.howDidYouHear === 'WTI Reference'">
                    <v-flex xs12 class="pb-3">
                      <div class="flabel popsemibold">Reference Name</div>
                      <v-text-field v-model="formData.referenceName" dense outlined hide-details color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                  </v-layout>

                  <v-layout wrap>
                    <v-flex xs12 class="pb-3">
                      <div class="flabel popsemibold">Legally eligible to work in India? *</div>
                      <v-radio-group v-model="formData.legallyEligible" row class="mt-1" hide-details :rules="[v => v !== null || 'Required']">
                        <v-radio label="Yes" :value="true" color="#005f32"></v-radio>
                        <v-radio label="No" :value="false" color="#005f32"></v-radio>
                      </v-radio-group>
                    </v-flex>
                  </v-layout>

                  <!-- Area of Interest — only on Career page -->
                  <template v-if="!hideAreaOfInterest">
                    <v-divider class="my-3" style="border-color: #e8f5ee;"></v-divider>
                    <div class="field-group-label popsemibold mb-2">Area of Interest</div>
                    <v-layout wrap>
                      <v-flex xs6 class="pr-2 pb-2">
                        <v-radio-group v-model="formData.areaOfInterest" class="mt-0" hide-details :rules="reqRule">
                          <v-radio label="Conservation" value="Conservation" color="#005f32"></v-radio>
                          <v-radio label="Support" value="Support" color="#005f32" class="mt-2"></v-radio>
                        </v-radio-group>
                      </v-flex>
                      <v-flex xs6 class="pl-2 pb-2">
                        <template v-if="formData.areaOfInterest === 'Conservation'">
                          <div class="flabel popregular">Division List</div>
                          <v-select :items="filteredDivisions" item-text="divisiontName" item-value="divisiontName" v-model="formData.divisionList" dense outlined hide-details color="#005f32" class="wti-input mb-2"></v-select>
                          <div class="flabel popregular mt-1">Project List</div>
                          <v-select :items="filteredProjects" item-text="projectName" item-value="projectName" v-model="formData.projectList" dense outlined hide-details color="#005f32" class="wti-input"></v-select>
                        </template>
                        <template v-if="formData.areaOfInterest === 'Support'">
                          <div class="flabel popregular">Division List</div>
                          <v-select :items="filteredDivisions" item-text="divisiontName" item-value="divisiontName" v-model="formData.divisionList" dense outlined hide-details color="#005f32" class="wti-input"></v-select>
                        </template>
                      </v-flex>
                    </v-layout>
                  </template>

                </div>
              </div>
            </v-flex>

            <!-- ─── COL 2: Education & Key Skills ─────────────────────── -->
            <v-flex xs12 md4 class="form-col px-md-2 mb-5">
              <div class="section-card">
                <div class="section-head">
                  <v-icon left size="18" color="#ffffff">mdi-school</v-icon>
                  <span class="popsemibold">EDUCATION &amp; KEY SKILLS</span>
                </div>
                <div class="section-body">

                  <div class="field-group-label popsemibold mb-3">Educational Background</div>

                  <!-- Edu row helper -->
                  <div class="edu-row mb-3">
                    <v-checkbox v-model="formData.graduate" label="Graduate" class="edu-check" hide-details color="#005f32"></v-checkbox>
                    <v-text-field v-model="formData.graduateSubject" :disabled="!formData.graduate" placeholder="Subject" dense outlined hide-details color="#005f32" class="wti-input edu-subject"></v-text-field>
                    <v-select :items="years" v-model="formData.graduateYear" :disabled="!formData.graduate" placeholder="Year" dense outlined hide-details color="#005f32" class="wti-input edu-year"></v-select>
                  </div>
                  <div class="edu-row mb-3">
                    <v-checkbox v-model="formData.postGraduate" label="Post Graduate" class="edu-check" hide-details color="#005f32"></v-checkbox>
                    <v-text-field v-model="formData.postGraduateSubject" :disabled="!formData.postGraduate" placeholder="Subject" dense outlined hide-details color="#005f32" class="wti-input edu-subject"></v-text-field>
                    <v-select :items="years" v-model="formData.postGraduateYear" :disabled="!formData.postGraduate" placeholder="Year" dense outlined hide-details color="#005f32" class="wti-input edu-year"></v-select>
                  </div>
                  <div class="edu-row mb-4">
                    <v-checkbox v-model="formData.others" label="PhD / PGD" class="edu-check" hide-details color="#005f32"></v-checkbox>
                    <v-text-field v-model="formData.othersSubject" :disabled="!formData.others" placeholder="Subject" dense outlined hide-details color="#005f32" class="wti-input edu-subject"></v-text-field>
                    <v-select :items="years" v-model="formData.othersYear" :disabled="!formData.others" placeholder="Year" dense outlined hide-details color="#005f32" class="wti-input edu-year"></v-select>
                  </div>

                  <div class="flabel popsemibold mb-1">Additional qualifications</div>
                  <v-text-field v-model="formData.additionalQualifications" dense outlined hide-details color="#005f32" class="wti-input mb-4"></v-text-field>

                  <v-divider class="my-4" style="border-color: #e8f5ee;"></v-divider>

                  <div class="field-group-label popsemibold mb-3">Key Professional Skills</div>
                  <v-layout wrap>
                    <v-flex xs6 v-for="skill in skillsList" :key="skill" class="pb-1">
                      <v-checkbox v-model="formData.keyProfessionalSkills" :value="skill" :label="skill" class="skill-check" hide-details color="#005f32"></v-checkbox>
                    </v-flex>
                  </v-layout>

                </div>
              </div>
            </v-flex>

            <!-- ─── COL 3: Professional Experience + Submit ────────────── -->
            <v-flex xs12 md4 class="form-col pl-md-3 mb-5">
              <div class="section-card" style="height: 100%; display: flex; flex-direction: column;">
                <div class="section-head">
                  <v-icon left size="18" color="#ffffff">mdi-briefcase</v-icon>
                  <span class="popsemibold">PROFESSIONAL EXPERIENCE</span>
                </div>
                <div class="section-body" style="flex: 1; display: flex; flex-direction: column;">

                  <v-layout wrap align-center class="mb-3">
                    <v-flex xs7 class="pr-3">
                      <div class="flabel popsemibold">Total years of experience *</div>
                    </v-flex>
                    <v-flex xs5>
                      <v-select :items="experienceYears" v-model="formData.totalYearsOfExperience" :rules="reqRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-select>
                    </v-flex>
                  </v-layout>

                  <div class="flabel popsemibold mb-1">Current/most recent position title</div>
                  <v-text-field v-model="formData.currentPositionTitle" dense outlined hide-details color="#005f32" class="wti-input mb-3"></v-text-field>

                  <div class="flabel popsemibold mb-1">Current/most recent employer name</div>
                  <v-text-field v-model="formData.currentEmployerName" dense outlined hide-details color="#005f32" class="wti-input mb-3"></v-text-field>

                  <v-layout wrap class="mb-3">
                    <v-flex xs4 class="pr-1">
                      <div class="flabel popsemibold">Current CTC</div>
                      <div class="flabel popregular" style="font-size:10px; color:#888;">(in LPA)</div>
                      <v-text-field type="number" v-model="formData.currentCTC" :rules="numberRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                    <v-flex xs4 class="px-1">
                      <div class="flabel popsemibold">Expected CTC</div>
                      <div class="flabel popregular" style="font-size:10px; color:#888;">(in LPA)</div>
                      <v-text-field type="number" v-model="formData.expectedCTC" :rules="numberRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                    <v-flex xs4 class="pl-1">
                      <div class="flabel popsemibold">Notice Period</div>
                      <div class="flabel popregular" style="font-size:10px; color:#888;">(in days)</div>
                      <v-text-field type="number" v-model="formData.noticePeriod" :rules="numberRule" dense outlined hide-details="auto" color="#005f32" class="wti-input"></v-text-field>
                    </v-flex>
                  </v-layout>

                  <div class="flabel popsemibold mb-1">Key responsibilities in current/most recent role *</div>
                  <v-textarea v-model="formData.keyResponsibilities" :rules="reqRule" dense outlined rows="4" hint="max 200 words" persistent-hint color="#005f32" class="wti-input mb-3"></v-textarea>

                  <div class="field-group-label popsemibold mb-2">Experience with specific species/habitats</div>
                  <v-layout wrap class="mb-4">
                    <v-flex xs6 v-for="species in speciesList" :key="species" class="pb-1">
                      <v-checkbox v-model="formData.experienceWithSpecies" :value="species" :label="species" class="skill-check" hide-details color="#005f32"></v-checkbox>
                    </v-flex>
                  </v-layout>

                  <v-spacer></v-spacer>

                  <!-- CV Upload -->
                  <div class="upload-zone pa-4 mb-4 text-center" @click="$refs.cvUpload.click()">
                    <input type="file" ref="cvUpload" accept=".pdf,.doc,.docx" class="d-none" @change="handleFileUpload">
                    <v-icon size="36" color="#005f32" class="mb-2">mdi-file-upload-outline</v-icon>
                    <div class="popsemibold" style="color: #005f32; font-size: 14px;">Upload CV / Resume</div>
                    <div class="popregular" style="font-size: 12px; color: #777; margin-top: 4px;">PDF preferred · Max 3MB</div>
                    <v-expand-transition>
                      <div v-if="cvFile" class="cv-success mt-3 d-flex align-center">
                        <v-icon small color="#005f32" class="mr-1">mdi-check-circle</v-icon>
                        <span class="popregular text-truncate" style="font-size:12px; color: #005f32;">{{ cvFile.name }}</span>
                        <v-spacer></v-spacer>
                        <v-icon small color="#c0392b" @click.stop="removeFile" style="cursor:pointer;">mdi-close</v-icon>
                      </div>
                    </v-expand-transition>
                  </div>

                  <!-- Submit -->
                  <v-btn block dark x-large class="submit-btn popsemibold" @click="submitForm" :loading="submitting">
                    SUBMIT APPLICATION
                    <v-icon right>mdi-arrow-right-bold</v-icon>
                  </v-btn>

                </div>
              </div>
            </v-flex>

          </v-layout>
        </v-form>
      </v-container>
    </div>

    <v-snackbar v-model="showSnackBar" color="#005f32" right :timeout="5000">
      <span class="popregular" style="color:#fff;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackBar = false"><v-icon color="#fff">mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "JobApplicationForm",
  props: {
    hideAreaOfInterest: { type: Boolean, default: false },
    jobId:             { type: String,  default: null },
    jobCategory:       { type: String,  default: null }
  },
  data() {
    return {
      valid: true,
      submitting: false,
      showSnackBar: false,
      msg: "",
      cvFile: null,
      dobMenu: false,

      formData: {
        firstName: "", lastName: "", dob: null, age: null,
        email: "", contactNumber: "", countryCode: "+91",
        currentLocationState: "", currentLocationCity: "",
        permanentLocationState: "", permanentLocationCity: "",
        willingToRelocate: null,
        preferredLocations: [{ state: "", city: "" }],
        howDidYouHear: "", referenceName: "",
        legallyEligible: null,
        areaOfInterest: "", divisionList: "", projectList: "",

        graduate: false, graduateSubject: "", graduateYear: "",
        postGraduate: false, postGraduateSubject: "", postGraduateYear: "",
        others: false, othersSubject: "", othersYear: "",
        additionalQualifications: "",
        keyProfessionalSkills: [],

        totalYearsOfExperience: "",
        currentPositionTitle: "", currentEmployerName: "",
        currentCTC: "", expectedCTC: "", noticePeriod: "",
        keyResponsibilities: "",
        experienceWithSpecies: []
      },

      reqRule:    [ v => !!v || "This field is required" ],
      emailRules: [ v => !!v || "E-mail is required", v => /.+@.+/.test(v) || "Must be a valid email" ],
      phoneRules: [ v => !!v || "Phone is required", v => String(v).length >= 10 || "Must be at least 10 digits" ],
      numberRule: [ v => !v || /^[0-9]+(\.[0-9]+)?$/.test(v) || "Numbers only" ],

      countryCodes: [
        { code: '+93', label: 'Afghanistan (+93)' }, { code: '+355', label: 'Albania (+355)' }, { code: '+213', label: 'Algeria (+213)' }, { code: '+376', label: 'Andorra (+376)' }, { code: '+244', label: 'Angola (+244)' }, { code: '+1-268', label: 'Antigua and Barbuda (+1-268)' }, { code: '+54', label: 'Argentina (+54)' }, { code: '+374', label: 'Armenia (+374)' }, { code: '+61', label: 'Australia (+61)' }, { code: '+43', label: 'Austria (+43)' }, { code: '+994', label: 'Azerbaijan (+994)' }, { code: '+1-242', label: 'Bahamas (+1-242)' }, { code: '+973', label: 'Bahrain (+973)' }, { code: '+880', label: 'Bangladesh (+880)' }, { code: '+1-246', label: 'Barbados (+1-246)' }, { code: '+375', label: 'Belarus (+375)' }, { code: '+32', label: 'Belgium (+32)' }, { code: '+501', label: 'Belize (+501)' }, { code: '+229', label: 'Benin (+229)' }, { code: '+975', label: 'Bhutan (+975)' }, { code: '+591', label: 'Bolivia (+591)' }, { code: '+387', label: 'Bosnia and Herzegovina (+387)' }, { code: '+267', label: 'Botswana (+267)' }, { code: '+55', label: 'Brazil (+55)' }, { code: '+673', label: 'Brunei (+673)' }, { code: '+359', label: 'Bulgaria (+359)' }, { code: '+226', label: 'Burkina Faso (+226)' }, { code: '+257', label: 'Burundi (+257)' }, { code: '+238', label: 'Cabo Verde (+238)' }, { code: '+855', label: 'Cambodia (+855)' }, { code: '+237', label: 'Cameroon (+237)' }, { code: '+1', label: 'Canada (+1)' }, { code: '+236', label: 'Central African Republic (+236)' }, { code: '+235', label: 'Chad (+235)' }, { code: '+56', label: 'Chile (+56)' }, { code: '+86', label: 'China (+86)' }, { code: '+57', label: 'Colombia (+57)' }, { code: '+269', label: 'Comoros (+269)' }, { code: '+242', label: 'Congo (+242)' }, { code: '+506', label: 'Costa Rica (+506)' }, { code: '+385', label: 'Croatia (+385)' }, { code: '+53', label: 'Cuba (+53)' }, { code: '+357', label: 'Cyprus (+357)' }, { code: '+420', label: 'Czech Republic (+420)' }, { code: '+45', label: 'Denmark (+45)' }, { code: '+253', label: 'Djibouti (+253)' }, { code: '+1-767', label: 'Dominica (+1-767)' }, { code: '+1-809', label: 'Dominican Republic (+1-809)' }, { code: '+593', label: 'Ecuador (+593)' }, { code: '+20', label: 'Egypt (+20)' }, { code: '+503', label: 'El Salvador (+503)' }, { code: '+240', label: 'Equatorial Guinea (+240)' }, { code: '+291', label: 'Eritrea (+291)' }, { code: '+372', label: 'Estonia (+372)' }, { code: '+268', label: 'Eswatini (+268)' }, { code: '+251', label: 'Ethiopia (+251)' }, { code: '+679', label: 'Fiji (+679)' }, { code: '+358', label: 'Finland (+358)' }, { code: '+33', label: 'France (+33)' }, { code: '+241', label: 'Gabon (+241)' }, { code: '+220', label: 'Gambia (+220)' }, { code: '+995', label: 'Georgia (+995)' }, { code: '+49', label: 'Germany (+49)' }, { code: '+233', label: 'Ghana (+233)' }, { code: '+30', label: 'Greece (+30)' }, { code: '+1-473', label: 'Grenada (+1-473)' }, { code: '+502', label: 'Guatemala (+502)' }, { code: '+224', label: 'Guinea (+224)' }, { code: '+245', label: 'Guinea-Bissau (+245)' }, { code: '+592', label: 'Guyana (+592)' }, { code: '+509', label: 'Haiti (+509)' }, { code: '+504', label: 'Honduras (+504)' }, { code: '+36', label: 'Hungary (+36)' }, { code: '+354', label: 'Iceland (+354)' }, { code: '+91', label: 'India (+91)' }, { code: '+62', label: 'Indonesia (+62)' }, { code: '+98', label: 'Iran (+98)' }, { code: '+964', label: 'Iraq (+964)' }, { code: '+353', label: 'Ireland (+353)' }, { code: '+972', label: 'Israel (+972)' }, { code: '+39', label: 'Italy (+39)' }, { code: '+1-876', label: 'Jamaica (+1-876)' }, { code: '+81', label: 'Japan (+81)' }, { code: '+962', label: 'Jordan (+962)' }, { code: '+7', label: 'Kazakhstan (+7)' }, { code: '+254', label: 'Kenya (+254)' }, { code: '+686', label: 'Kiribati (+686)' }, { code: '+965', label: 'Kuwait (+965)' }, { code: '+996', label: 'Kyrgyzstan (+996)' }, { code: '+856', label: 'Laos (+856)' }, { code: '+371', label: 'Latvia (+371)' }, { code: '+961', label: 'Lebanon (+961)' }, { code: '+266', label: 'Lesotho (+266)' }, { code: '+231', label: 'Liberia (+231)' }, { code: '+218', label: 'Libya (+218)' }, { code: '+423', label: 'Liechtenstein (+423)' }, { code: '+370', label: 'Lithuania (+370)' }, { code: '+352', label: 'Luxembourg (+352)' }, { code: '+261', label: 'Madagascar (+261)' }, { code: '+265', label: 'Malawi (+265)' }, { code: '+60', label: 'Malaysia (+60)' }, { code: '+960', label: 'Maldives (+960)' }, { code: '+223', label: 'Mali (+223)' }, { code: '+356', label: 'Malta (+356)' }, { code: '+692', label: 'Marshall Islands (+692)' }, { code: '+222', label: 'Mauritania (+222)' }, { code: '+230', label: 'Mauritius (+230)' }, { code: '+52', label: 'Mexico (+52)' }, { code: '+691', label: 'Micronesia (+691)' }, { code: '+373', label: 'Moldova (+373)' }, { code: '+377', label: 'Monaco (+377)' }, { code: '+976', label: 'Mongolia (+976)' }, { code: '+382', label: 'Montenegro (+382)' }, { code: '+212', label: 'Morocco (+212)' }, { code: '+258', label: 'Mozambique (+258)' }, { code: '+95', label: 'Myanmar (+95)' }, { code: '+264', label: 'Namibia (+264)' }, { code: '+674', label: 'Nauru (+674)' }, { code: '+977', label: 'Nepal (+977)' }, { code: '+31', label: 'Netherlands (+31)' }, { code: '+64', label: 'New Zealand (+64)' }, { code: '+505', label: 'Nicaragua (+505)' }, { code: '+227', label: 'Niger (+227)' }, { code: '+234', label: 'Nigeria (+234)' }, { code: '+850', label: 'North Korea (+850)' }, { code: '+389', label: 'North Macedonia (+389)' }, { code: '+47', label: 'Norway (+47)' }, { code: '+968', label: 'Oman (+968)' }, { code: '+92', label: 'Pakistan (+92)' }, { code: '+680', label: 'Palau (+680)' }, { code: '+970', label: 'Palestine (+970)' }, { code: '+507', label: 'Panama (+507)' }, { code: '+675', label: 'Papua New Guinea (+675)' }, { code: '+595', label: 'Paraguay (+595)' }, { code: '+51', label: 'Peru (+51)' }, { code: '+63', label: 'Philippines (+63)' }, { code: '+48', label: 'Poland (+48)' }, { code: '+351', label: 'Portugal (+351)' }, { code: '+974', label: 'Qatar (+974)' }, { code: '+40', label: 'Romania (+40)' }, { code: '+7', label: 'Russia (+7)' }, { code: '+250', label: 'Rwanda (+250)' }, { code: '+1-869', label: 'Saint Kitts and Nevis (+1-869)' }, { code: '+1-758', label: 'Saint Lucia (+1-758)' }, { code: '+1-784', label: 'Saint Vincent and the Grenadines (+1-784)' }, { code: '+685', label: 'Samoa (+685)' }, { code: '+378', label: 'San Marino (+378)' }, { code: '+239', label: 'Sao Tome and Principe (+239)' }, { code: '+966', label: 'Saudi Arabia (+966)' }, { code: '+221', label: 'Senegal (+221)' }, { code: '+381', label: 'Serbia (+381)' }, { code: '+248', label: 'Seychelles (+248)' }, { code: '+232', label: 'Sierra Leone (+232)' }, { code: '+65', label: 'Singapore (+65)' }, { code: '+421', label: 'Slovakia (+421)' }, { code: '+386', label: 'Slovenia (+386)' }, { code: '+677', label: 'Solomon Islands (+677)' }, { code: '+252', label: 'Somalia (+252)' }, { code: '+27', label: 'South Africa (+27)' }, { code: '+82', label: 'South Korea (+82)' }, { code: '+211', label: 'South Sudan (+211)' }, { code: '+34', label: 'Spain (+34)' }, { code: '+94', label: 'Sri Lanka (+94)' }, { code: '+249', label: 'Sudan (+249)' }, { code: '+597', label: 'Suriname (+597)' }, { code: '+46', label: 'Sweden (+46)' }, { code: '+41', label: 'Switzerland (+41)' }, { code: '+963', label: 'Syria (+963)' }, { code: '+886', label: 'Taiwan (+886)' }, { code: '+992', label: 'Tajikistan (+992)' }, { code: '+255', label: 'Tanzania (+255)' }, { code: '+66', label: 'Thailand (+66)' }, { code: '+670', label: 'Timor-Leste (+670)' }, { code: '+228', label: 'Togo (+228)' }, { code: '+676', label: 'Tonga (+676)' }, { code: '+1-868', label: 'Trinidad and Tobago (+1-868)' }, { code: '+216', label: 'Tunisia (+216)' }, { code: '+90', label: 'Turkey (+90)' }, { code: '+993', label: 'Turkmenistan (+993)' }, { code: '+688', label: 'Tuvalu (+688)' }, { code: '+256', label: 'Uganda (+256)' }, { code: '+380', label: 'Ukraine (+380)' }, { code: '+971', label: 'United Arab Emirates (+971)' }, { code: '+44', label: 'United Kingdom (+44)' }, { code: '+1', label: 'United States (+1)' }, { code: '+598', label: 'Uruguay (+598)' }, { code: '+998', label: 'Uzbekistan (+998)' }, { code: '+678', label: 'Vanuatu (+678)' }, { code: '+379', label: 'Vatican City (+379)' }, { code: '+58', label: 'Venezuela (+58)' }, { code: '+84', label: 'Vietnam (+84)' }, { code: '+967', label: 'Yemen (+967)' }, { code: '+260', label: 'Zambia (+260)' }, { code: '+263', label: 'Zimbabwe (+263)' }
      ],

      states:              [ "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal" ],
      hearSources:         [ "LinkedIn", "Social Media", "WTI Reference", "Job Portal", "Newspaper", "Friend/Colleague", "Other" ],
      divisionsList:       [],
      projectsList:        [],
      experienceYears:     Array.from({ length: 50 }, (_, i) => i + 1),
      years:               Array.from({ length: 30 }, (_, i) => String(new Date().getFullYear() - i)),
      skillsList: [
        "Conservation Biology & Ecology", "Population Estimation & Surveying", "Restoration Ecology & Habitat Management", 
        "Animal Behavior & Ethology", "Camera Trapping, Telemetry & Field Monitoring", "Wildlife Veterinary Medicine & Surgery", 
        "Rescue, Treatment & Rehabilitation", "Wildlife Health & Disease Management", "Hand-rearing of Orphaned Wildlife", 
        "Captive Animal Management", "Human-Wildlife Conflict (HWC) Mitigation", "Sociology & Rural Social Work", 
        "Community Engagement & Mobilization", "Awareness & Natural Heritage Campaigns", "Livelihood Generation & Development", 
        "GIS & Spatial Mapping", "Remote Sensing & Landscape Ecology", "Conservation Data Analysis & Statistics", 
        "Wildlife Crime Control & Investigation", "Anti-Poaching Strategy & Operations", "Protected Area Management & Recovery", 
        "Capacity Building (Frontline Staff Training)", "Environmental Law, Policy & Advocacy", "Conservation Project Management", 
        "Stakeholder & Government Liaison", "Fundraising & Grant Writing", "Conservation Communication & Journalism", 
        "Emergency Response & Crisis Management"
      ],
      speciesList: [ 
        "Big Cats", "Small & Lesser Cats", "Megaherbivores", "Mountain Ungulates & Cervids", 
        "Marine & Aquatic Species", "Primates", "Avian Species", "Reptiles & Amphibians", 
        "Wildlife Corridors", "Wetlands, Coastal & Marine", "Forest Ecosystems", "Grasslands", 
        "Montane & Alpine", "Protected Area Interfaces"
      ]
    };
  },
  computed: {
    filteredDivisions() {
      if (this.formData.areaOfInterest === 'Conservation') {
        return this.divisionsList.filter(d => 
          d.departmentId && d.departmentId.deptName && d.departmentId.deptName.toLowerCase() === 'conservation'
        );
      } else if (this.formData.areaOfInterest === 'Support') {
        return this.divisionsList.filter(d => 
          d.departmentId && d.departmentId.deptName && d.departmentId.deptName.toLowerCase() === 'management'
        );
      }
      return this.divisionsList;
    },
    filteredProjects() {
      if (this.formData.divisionList) {
        return this.projectsList.filter(p => 
          p.division && p.division.divisiontName === this.formData.divisionList
        );
      }
      return this.projectsList;
    }
  },
  watch: {
    'formData.areaOfInterest'() {
      this.formData.divisionList = "";
      this.formData.projectList = "";
    },
    'formData.divisionList'() {
      this.formData.projectList = "";
    }
  },
  created() {
    this.fetchDivisions();
    this.fetchProjects();
  },
  methods: {
    fetchDivisions() {
      axios.get('/division/list').then(response => {
        if (response.data && response.data.status) {
          this.divisionsList = response.data.data;
        }
      }).catch(err => console.error("Error fetching divisions:", err));
    },
    fetchProjects() {
      axios.get('/projects/getalllist').then(response => {
        if (response.data && response.data.status) {
          this.projectsList = response.data.data;
        }
      }).catch(err => console.error("Error fetching projects:", err));
    },
    addLocation() {
      if (this.formData.preferredLocations.length < 3) {
        this.formData.preferredLocations.push({ state: "", city: "" });
      }
    },
    removeLocation(index) {
      if (this.formData.preferredLocations.length > 1) {
        this.formData.preferredLocations.splice(index, 1);
      }
    },
    calculateAge() {
      if (!this.formData.dob) return;
      const today = new Date(), bd = new Date(this.formData.dob);
      let age = today.getFullYear() - bd.getFullYear();
      if (today.getMonth() < bd.getMonth() || (today.getMonth() === bd.getMonth() && today.getDate() < bd.getDate())) age--;
      this.formData.age = age;
    },
    handleFileUpload(e) {
      const f = e.target.files[0];
      if (!f) return;
      if (f.size > 3145728) { this.msg = "File size should be less than 3MB"; this.showSnackBar = true; this.$refs.cvUpload.value = []; return; }
      this.cvFile = f;
    },
    removeFile() { this.cvFile = null; if (this.$refs.cvUpload) this.$refs.cvUpload.value = []; },
    submitForm() {
      if (!this.$refs.form.validate()) { this.msg = "Please fill all required fields."; this.showSnackBar = true; return; }
      if (!this.cvFile)               { this.msg = "Please upload your CV / Resume!"; this.showSnackBar = true; return; }

      this.submitting = true;
      const payload = { ...this.formData };
      payload.contactNumber = `${this.formData.countryCode} ${this.formData.contactNumber}`;
      if (this.jobId)       payload.jobTitle    = this.jobId;
      if (this.jobCategory) payload.jobCategory = this.jobCategory;

      const endpoint = this.jobId ? "/job/application/apply" : "/job/application/career/apply";

      axios.post(endpoint, payload)
        .then(res => {
          if (res.data.status) {
            const id = res.data.data._id;
            if (id) { this.uploadResume(id); }
            else    { this.onSuccess(); }
          } else {
            this.submitting = false;
            this.msg = res.data.msg || "Failed to submit. Please try again.";
            this.showSnackBar = true;
          }
        })
        .catch(() => { this.submitting = false; this.msg = "An error occurred. Please try again."; this.showSnackBar = true; });
    },
    uploadResume(id) {
      const fd = new FormData(); fd.append("id", id); fd.append("doc", this.cvFile);
      axios.post("/job/application/resume", fd)
        .then(res => {
          this.submitting = false;
          this.msg = res.data.status ? "Application submitted successfully!" : "Submitted, but couldn't upload CV.";
          this.showSnackBar = true;
          this.onSuccess();
        })
        .catch(() => { this.submitting = false; this.msg = "Submitted, but couldn't upload CV."; this.showSnackBar = true; });
    },
    onSuccess() {
      this.$refs.form.reset();
      this.removeFile();
      Object.assign(this.formData, { keyProfessionalSkills: [], experienceWithSpecies: [] });
    }
  }
};
</script>

<style scoped>
/* ─── Page shell ─────────────────────────────────────────────── */
.form-page-wrapper {
  background-color: #f5f5f0;
  min-height: 100vh;
}

/* ─── Hero banner ─────────────────────────────────────────────── */
.form-hero {
  background-color: #005f32;
  position: relative;
  overflow: hidden;
}
.form-hero::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 4px;
  background: #e27826;
}
.hero-title {
  display: block;
  font-size: clamp(36px, 6vw, 72px);
  letter-spacing: 3px;
}
.hero-underline {
  width: 80px;
  height: 4px;
  background-color: #e27826;
}
.hero-sub {
  font-size: 16px;
  opacity: 0.88;
  letter-spacing: 0.5px;
}

/* ─── Section card ────────────────────────────────────────────── */
.section-card {
  background: #ffffff;
  border-top: 4px solid #005f32;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  height: 100%;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}
.section-head {
  background-color: #005f32;
  color: #ffffff;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  letter-spacing: 1.2px;
  font-size: 13px;
}
.section-body {
  padding: 16px;
}

/* ─── Labels ──────────────────────────────────────────────────── */
.flabel {
  font-size: 12px;
  color: #333;
  margin-bottom: 3px;
  letter-spacing: 0.3px;
  text-align: left;
  display: block;
}
.field-group-label {
  font-size: 12.5px;
  color: #005f32;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 1px solid #e0ece6;
  padding-bottom: 4px;
  text-align: left;
  display: block;
}

/* ─── Age chip ────────────────────────────────────────────────── */
.age-chip {
  display: inline-block;
  background: #e8f5ee;
  color: #005f32;
  border: 1px solid #c3ddd0;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 2px;
  min-width: 60px;
  text-align: center;
}

/* ─── Input overrides — rounded + shorter height ──────────────── */
.wti-input ::v-deep .v-input__slot {
  background-color: #fcfcfc !important;
  transition: background 0.2s ease;
  border-radius: 5px !important;  /* override global border-radius: 0 */
}
.wti-input:not(.v-textarea) ::v-deep .v-input__slot {
  min-height: 34px !important;
  height: 34px !important;
}
.wti-input:not(.v-textarea) ::v-deep .v-input__control {
  min-height: 34px !important;
  /* height removed to allow validation messages to expand below the slot */
}
/* Compact the validation error message container */
.wti-input ::v-deep .v-text-field__details {
  padding-top: 2px !important;
  margin-bottom: 2px !important;
}
.wti-input ::v-deep .v-messages {
  font-size: 10.5px !important;
  line-height: 1.1 !important;
  min-height: 12px !important;
}
/* Ensure inner slots fill the height and center content vertically */
.wti-input:not(.v-textarea) ::v-deep .v-text-field__slot,
.wti-input:not(.v-textarea) ::v-deep .v-select__slot,
.wti-input:not(.v-textarea) ::v-deep .v-select__selections {
  min-height: 34px !important;
  height: 34px !important;
  display: flex !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important;
}
/* Strip margin/padding from the actual input elements and center them */
.wti-input:not(.v-textarea) ::v-deep input {
  padding: 0 !important;
  margin: 0 !important;
  font-size: 13px;
  height: auto !important;
  max-height: 100% !important;
}
.wti-input.v-textarea ::v-deep textarea {
  font-size: 13px;
}
/* Darker placeholder text for better visibility */
.wti-input ::v-deep input::placeholder {
  color: #777 !important;
  opacity: 1 !important;
}
/* Align select placeholder and selected text */
.wti-input ::v-deep .v-select__selection {
  font-size: 13px;
  margin: 0 !important;
  padding: 0 !important;
  line-height: normal;
}
.wti-input ::v-deep .v-select__selection.v-select__selection--comma {
  color: #333 !important;
}
/* Select placeholder specifically */
.wti-input ::v-deep .v-select__selection[class*="--placeholder"] {
  color: #777 !important;
}
.wti-input ::v-deep .v-select__selection--comma {
  margin: 0 !important;
}
/* Ensure the append icon stays centered in the constrained height */
.wti-input ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
  display: flex;
  align-items: center;
}
.wti-input.calendar-input ::v-deep .v-icon {
  color: #005f32 !important;
  font-size: 18px !important;
}

/* ─── Square Calendar Overrides ───────────────────────────────── */
::v-deep .square-calendar {
  width: 230px !important;
  height: 230px !important;
  min-width: 230px !important;
}
::v-deep .square-calendar .v-date-picker-header {
  padding: 4px 12px 0 !important;
  margin-bottom: -4px !important;
}
::v-deep .square-calendar .v-date-picker-table {
  height: 180px !important;
  padding: 0 8px !important;
}
::v-deep .square-calendar .v-date-picker-table table {
  table-layout: fixed;
}
::v-deep .square-calendar .v-date-picker-table th,
::v-deep .square-calendar .v-date-picker-table td {
  height: 24px !important;
  padding: 0 !important;
}
::v-deep .square-calendar .v-date-picker-table .v-btn {
  height: 22px !important;
  width: 22px !important;
  font-size: 11px !important;
  margin: 0 !important;
}
::v-deep .square-calendar .v-date-picker-table th {
  font-size: 10px !important;
}

/* Round the fieldset border itself */
.wti-input ::v-deep fieldset {
  border-radius: 5px !important;
}
.wti-input ::v-deep .v-input__slot:hover {
  background-color: #fff !important;
}
.wti-input.v-input--is-focused ::v-deep fieldset {
  border-color: #005f32 !important;
  border-width: 2px !important;
}
/* Disabled state for clear contrast */
.wti-input.v-input--is-disabled ::v-deep .v-input__slot {
  background-color: #f2f5f3 !important;
  opacity: 0.7 !important;
}
.wti-input.v-input--is-disabled ::v-deep fieldset {
  border: 1px dashed #c0d1c7 !important;
}

/* ─── Radio overrides ─────────────────────────────────────────── */
::v-deep .v-radio .v-label {
  font-family: poppinsregular;
  font-size: 12.5px !important;
  color: #333 !important;
}

/* ─── Education row ───────────────────────────────────────────── */
.edu-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.edu-check { flex: 0 0 108px; }
.edu-subject { flex: 1; }
.edu-year { flex: 0 0 78px; }
.edu-check ::v-deep .v-label {
  font-family: poppinsregular;
  font-size: 12px !important;
  color: #333 !important;
}

/* ─── Skill checkboxes ────────────────────────────────────────── */
.skill-check ::v-deep .v-label {
  font-family: poppinsregular;
  font-size: 12px !important;
  color: #333 !important;
}

/* ─── Upload zone ─────────────────────────────────────────────── */
.upload-zone {
  border: 2px dashed #a5cbb8;
  border-radius: 8px;
  background: #f4fbf7;
  cursor: pointer;
  transition: all 0.25s ease;
}
.upload-zone:hover {
  border-color: #005f32;
  background: #eaf5ef;
}
.cv-success {
  background: #e8f5ee;
  border: 1px solid #b2d9c3;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
}

/* ─── Submit button ───────────────────────────────────────────── */
.submit-btn {
  background-color: #005f32 !important;
  color: #ffffff !important;
  letter-spacing: 2px;
  font-size: 14px !important;
  border-radius: 6px !important;
  transition: background-color 0.25s ease, transform 0.2s ease;
}
.submit-btn:hover {
  background-color: #e27826 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(226, 120, 38, 0.35) !important;
}
</style>
